import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'classnames';

import Pagination from 'components/ui/pagination';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle, faSearch } from '@fortawesome/pro-solid-svg-icons';

import LoadingSpinner from 'components/ui/loading_spinner';
import EmptyState from 'shared/empty_state';

import PropertiesBulkDeleteModal from './modals/properties_bulk_delete_modal';
import PropertiesBulkTaggingModal from './modals/properties_bulk_tagging_modal';
import PropertiesDeleteGroupModal from './modals/properties_delete_group_modal';
import PropertiesDeleteSavedSearch from './modals/delete_saved_search_modal';
import PropertiesGroupFormModal from './modals/properties_group_form_modal';
import CopyRecordsModal from './modals/copy_records_modal';
import MoveRecordsModal from './modals/move_records_modal';

import PropertiesPageHeader from './header';
import MobileResearchFilters from '../filters/mobile_research_filters';
import PropertiesMapContainer from './properties_map_container';
import PropertiesMapList from './properties_map_list';
import PropertiesTable from './properties_table';
import ResearchFilterTopbar from '../filters/research_filter_topbar';
import DuplicatePropertiesView from './duplicates/duplicate_properties_view';

import { changePropertyPage } from '../store/actions/query';
import { retrieveGroups } from '../store/actions/properties';
import { retrieveRecords, delayRetrieveRecords } from '../store/actions/search';
import { switchTab, toggleAlert } from '../store/actions/ui';
import {
  resetSelectedProperties,
  resetSelectedPropertiesOnCurrentPage,
} from '../store/actions/select_list';
import { isDuplicateViewActive } from '../store/selectors/duplicates';
import {
  faList,
  faLocationDot,
} from '@fortawesome/pro-regular-svg-icons';
import { Button, ButtonGroup, Col, Row } from 'react-bootstrap-5';

export class PropertiesTab extends React.Component {
  static propTypes = {
    currentPage: PropTypes.number.isRequired,
    currentTab: PropTypes.oneOf(['maps', 'properties']).isRequired,
    delayRetrieveRecords: PropTypes.func.isRequired,
    edgeCrm: PropTypes.bool,
    emptyStateHelpPath: PropTypes.string.isRequired,
    groups: PropTypes.array,
    initialFetch: PropTypes.bool,
    isDuplicateViewActive: PropTypes.bool,
    isFetchingProperties: PropTypes.bool,
    pageSize: PropTypes.number.isRequired,
    properties: PropTypes.array.isRequired,
    propertyIds: PropTypes.array.isRequired,
    resetSelection: PropTypes.func.isRequired,
    resetSelectionOnCurrentPage: PropTypes.func.isRequired,
    retrieveGroups: PropTypes.func.isRequired,
    retrieveRecords: PropTypes.func.isRequired,
    selectedGroupId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    selectedPropertyIds: PropTypes.arrayOf(PropTypes.number).isRequired,
    svnMember: PropTypes.bool,
    switchTab: PropTypes.func.isRequired,
    toggleAlert: PropTypes.func.isRequired,
    totalCount: PropTypes.number.isRequired,
    totalPages: PropTypes.number.isRequired,
    totalUngroupedCount: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired,
  };

  scrollPanel = React.createRef();

  state = {
    displayMobileFilters: false,
  };

  componentDidMount() {
    this.props.resetSelection();
  }

  findSelectedGroup() {
    const { groups, selectedGroupId } = this.props;
    if (selectedGroupId) {
      return groups.find(group => group.id === selectedGroupId);
    }
  }

  handlePageChange = (page) => {
    const { onPageChange, retrieveRecords } = this.props;
    onPageChange(page);
    retrieveRecords();
    this.scrollToTop();
  };

  scrollToTop() {
    this.scrollPanel.current.scrollTop = 0;
  }

  mobileTabClass(tab) {
    const { currentTab } = this.props;

    return classNames({
      'mobile-icons': true,
      active: currentTab === tab,
    });
  }

  handleDisplayMobileFilters = () => this.setState({ displayMobileFilters: true });
  handleCloseMobileFilters = () => this.setState({ displayMobileFilters: false });

  render() {
    return (
      <div className="d-flex flex-column h-100">
        {this.props.isDuplicateViewActive ?
          <DuplicatePropertiesView /> :
          <>
            <PropertiesPageHeader />
            {this.renderFilterTopBar()}
            {this.renderMobileTabIcons()}
            <div className="d-flex flex-column flex-grow-1 overflow-hidden" ref={this.scrollPanel}>
              {this.renderActiveTab()}
            </div>
            {this.state.displayMobileFilters && this.renderMobileFilters()}
            <PropertiesDeleteGroupModal />
            <PropertiesGroupFormModal />
            <PropertiesBulkTaggingModal />
            <PropertiesBulkDeleteModal />
            <PropertiesDeleteSavedSearch />
            <CopyRecordsModal />
            <MoveRecordsModal />
          </>
        }
      </div>
    );
  }

  renderMobileFilters() {
    const { totalCount } = this.props;

    return (
      <div className="d-lg-none mobile-content">
        <MobileResearchFilters
          propertyCount={totalCount}
          onCloseFilters={this.handleCloseMobileFilters}
        />
      </div>
    );
  }

  renderFilterTopBar() {
    const { currentPage, pageSize, properties, totalCount } = this.props;

    const pageData = {
      currentPage: currentPage,
      itemCount: properties.length,
      itemSingularLabel: 'property',
      pageSize: pageSize,
      totalCount: totalCount,
    };

    return (
      <ResearchFilterTopbar
        edgeCrm={this.props.edgeCrm}
        pageData={pageData}
        propertyCount={totalCount}
        onDisplayFilters={this.handleDisplayMobileFilters}
      />
    );
  }

  renderActiveTab() {
    const { currentTab } = this.props;

    switch (currentTab) {
    case 'properties':
      return (
        <React.Fragment>
          {this.renderResults()}
          {this.renderPagination()}
        </React.Fragment>
      );
    case 'maps':
      return (
        <div className="maps-container d-flex h-100">
          <Row className="flex-grow-1 g-0">
            <Col className="d-flex flex-column h-100" lg={4} md={6}>
              <PropertiesMapList onPageChange={this.handlePageChange} />
            </Col>
            <Col className="h-100" lg={8} md={6}>
              <div className="maps-view w-100 h-100 position-relative">
                <PropertiesMapContainer name="maps" />
              </div>
            </Col>
          </Row>
        </div>
      );
    }
  }

  renderResults() {
    const {
      isFetchingProperties,
      properties,
      propertyIds,
      resetSelection,
      resetSelectionOnCurrentPage,
      svnMember,
      emptyStateHelpPath,
    } = this.props;

    if (isFetchingProperties) {
      return <LoadingSpinner className="flex-fill" />;
    } else if (properties.length > 0) {
      return (
        <PropertiesTable
          name="properties"
          properties={properties}
          propertyIds={propertyIds}
          resetSelection={resetSelection}
          resetSelectionOnCurrentPage={resetSelectionOnCurrentPage}
        />
      );
    } else {
      return (
        <EmptyState
          bodyText="Search with different criteria or create a New Property"
          className="m-10"
          header="No results found"
          icon={faSearch}
        >
          {svnMember && (
            <a className="btn btn-outline" href={emptyStateHelpPath}>
              <FontAwesomeIcon icon={faQuestionCircle} />
              How to use your Property Database
            </a>
          )}
        </EmptyState>
      );
    }
  }

  renderMobileTabIcons() {
    const { switchTab } = this.props;

    return (
      <div className="d-lg-none p-3 bg-white border-bottom">
        <ButtonGroup aria-label="Switch Properties View" className="w-100">
          <Button
            className={this.mobileTabClass('maps')}
            variant="outline"
            onClick={() => switchTab('maps')}
          >
            <FontAwesomeIcon icon={faLocationDot} />
            Map
          </Button>
          <Button
            className={this.mobileTabClass('properties')}
            variant="outline"
            onClick={() => switchTab('properties')}
          >
            <FontAwesomeIcon icon={faList} />
            List
          </Button>
        </ButtonGroup>
      </div>
    );
  }

  renderPagination() {
    const { properties, totalCount, currentPage, totalPages } = this.props;

    if (properties.length < totalCount) {
      return (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onChange={this.handlePageChange}
        />
      );
    }
  }
}

const mapStateToProps = state => ({
  ...state.properties,
  ...state.search.meta,

  currentTab: state.ui.currentTab,
  edgeCrm: state.features.edgeCrm,
  emptyStateHelpPath: state.paths.emptyStateHelp,
  initialFetch: state.search.initialFetchComplete,
  isDuplicateViewActive: isDuplicateViewActive(state),
  isFetchingProperties: state.search.isFetching,
  properties: state.search.properties,
  selectedGroupId: state.query.researchGroupId,
  selectedPropertyIds: state.selectList.selectedPropertyIds,
  svnMember: state.ui.svnMember,
});

const mapDispatchToProps = {
  delayRetrieveRecords,
  retrieveGroups,
  retrieveRecords,
  switchTab,
  toggleAlert,

  onPageChange: changePropertyPage,
  resetSelection: resetSelectedProperties,
  resetSelectionOnCurrentPage: resetSelectedPropertiesOnCurrentPage,
};

export default connect(mapStateToProps, mapDispatchToProps)(PropertiesTab);
